import React, { useEffect, ReactNode, useState, useMemo } from "react";

import {
    RegUIClientProvider,
    FullRegUISessionDetails,
    useRegUIClient
} from "@foundation/reg-ui-client";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { AbbaProvider, useAbba } from "@foundation/abba-client";
import { PersonalizationClient } from "@foundation/personalization";

import { NAV_ENVIRONMENT, User } from "./types";
import { NavContext } from "./NavContext";
import NavSignInClient from "./NavSignInClient";

type NavContextProviderProps = {
    children: ReactNode;
    environment?: NAV_ENVIRONMENT;
    menuItems?: Record<string, any>;
    abbaPlatformObject?: string;
    user?: User;
};

type WithRegionProps = {
    children: ReactNode;
    user?: User;
    navSignInClient: NavSignInClient;
};

type WithAbbaProps = {
    children: ReactNode;
    abbaPlatformObject?: string;
    navSignInClient: NavSignInClient;
};

const NAV_BAR = "nav-bar";

const WithRegion = ({ children, user, navSignInClient }: WithRegionProps) => {
    const searchClient = useMemo(() => new MediaServicesSearchClient(), []);
    const [personalizationClient, setPersonalizationClient] = useState<PersonalizationClient | undefined>();

    useEffect(() => {
        setPersonalizationClient(new PersonalizationClient({
            application: "nav",
            clientUrl: process.env.NEXT_PUBLIC_PERSONALIZATION_URL || ""
        }));
    }, []);

    const subscribeUrl = useAbba().abbaConfig?.["navi-subscription"]?.subscribeUrl;

    if (subscribeUrl) {
        navSignInClient.setSubscriptionUrl(subscribeUrl as string);
    }

    const navContext = useMemo(() => ({
        user,
        searchClient,
        personalizationClient,
        initializeSignInWithRegUI: navSignInClient.setSignInButton,
        onSignOut: navSignInClient.onSignOut,
        subscriptionUrl: navSignInClient.getSubscriptionUrl(),
        upgradeUrl: navSignInClient.getUpgradeUrl(),
    }), [user, searchClient, navSignInClient, personalizationClient]);

    return (
        <NavContext.Provider value={navContext}>
            {children}
        </NavContext.Provider>
    );
};

export const WithAbba = ({ abbaPlatformObject = NAV_BAR, children, navSignInClient }: WithAbbaProps) => {
    const { session = {}, client: regUIClient } = useRegUIClient();
    const {
        user,
        isLoggedIn,
        subscriptionsData,
        entitlementFeatures,
    } = (session as FullRegUISessionDetails);
    const isSubscriber = Boolean(subscriptionsData?.length && subscriptionsData.length > 0);
    const showUpgrade = entitlementFeatures?.length > 0;

    const navUser = useMemo(() => ({
        isLoggedIn,
        isSubscriber,
        firstName: user?.firstName,
        showUpgrade,
        subscriptionsData
    }), [session]);

    return (
        <AbbaProvider platform={abbaPlatformObject} user={regUIClient?.fetchAbbaUserInfo()}>
            <WithRegion
                children={children}
                user={navUser}
                navSignInClient={navSignInClient}
            />
        </AbbaProvider>
    );
};

export const NavProvider = ({
    children,
    abbaPlatformObject = NAV_BAR,
}: NavContextProviderProps) => {
    const navSignInClient = useMemo(() => new NavSignInClient(), []);

    return (
        <RegUIClientProvider onUserInfoReady={navSignInClient.setOnUserInfoReady}>
            <WithAbba
                abbaPlatformObject={abbaPlatformObject}
                children={children}
                navSignInClient={navSignInClient}
            />
        </RegUIClientProvider>
    );
};
